// 파트너 포털 내비게이션 — 순수 상수·판정 (IO 없음). 2026-09-10 N3 「좌측 레일」 채택 · 2026-09-11 유형별 메뉴
//   PC:   PartnerLayout → PartnerRail(좌측 레일)  · 휴대폰: BottomNavBar 파트너 탭(최대 4개)
//   문서: docs/pages/main/PartnerHub.md 「라우팅·내비」 · 시안 docs/design/partner-topnav-variants.html (N3)
//
// 🔒 접근 자체를 막는 규칙은 ProtectedRoute(허용 경로 목록)가 담당한다 — 여기는 「무엇을 보여 줄지」만 정한다.
//    두 목록이 어긋나면 레일에 있는 메뉴가 열리지 않으므로, 경로를 더할 때 ProtectedRoute 의 partnerAllowed 도 함께 본다.
// 🔒 유형 규칙(확인 뒤 partner_type · 확인 전 declared_type)은 partnerHub.partnerTypeOf 와 같다 — 순환 import 를 피해 여기 복제하고 테스트로 묶는다.
import { Home, BookOpen, Briefcase, Users, DollarSign, User, Lock, MessageSquare, BarChart2, TrendingUp, Folder } from "react-feather";
import { tt } from "../i18n/tt";
import { lazyI18n } from "../i18n/lazyText";

/**
 * Partner 「단독」 계정 — ProtectedRoute 잠금(파트너 경로 밖으로 못 나감)의 대상.
 *   Admin 겸직은 종전부터 제외. **Creator 겸직도 제외**(2026-09-21 대표 결정 — 기획 §7 결정 ⑥ 「불허」를 「허용」으로 뒤집음):
 *   크리에이터 겸 파트너는 크리에이터 화면을 그대로 쓰고, /partner* 에서만 파트너 레이아웃을 본다.
 */
export function isPartnerOnly(role) {
  const r = role || "";
  return r.includes("Partner") && !r.includes("Admin") && !r.includes("Creator");
}

/**
 * 두 화면 사이의 「문」 — 2026-09-28 대표 결정 「파트너도 크리에이터처럼」(docs/ops/partner-works-browse-plan.md §4-2).
 *   showsPartnerCenterDoor: 크리에이터 화면(상단바·사이드바·≡ 메뉴)에 「파트너센터」 — Partner 역할이 있으면(Admin 겸직 포함 — 크리에이터 레이아웃을 쓰는 어드민도 /partner 로 가는 문이 필요하다).
 *   showsCreatorHomeDoor:  파트너 화면(상단 스트립·레일·홈 안내 카드)에 「크리에이터 홈 →」 — Creator 겸직만. Admin 은 「어드민 확인용」 띠(PartnerAdminPreviewBand)에 같은 링크가 있어 중복하지 않고,
 *                          Partner 단독은 크리에이터 화면이 잠겨 있어(isPartnerOnly) 문을 내지 않는다.
 *   다섯 화면(TopNavBar · UserSidebar · PartnerTopStrip · PartnerRail · PartnerCreatorHomeCard)이 이 두 함수만 본다 — 한 화면에만 문이 생기거나 사라지지 않게.
 */
export function showsPartnerCenterDoor(role) {
  return (role || "").includes("Partner");
}

export function showsCreatorHomeDoor(role) {
  const r = role || "";
  return r.includes("Creator") && r.includes("Partner") && !r.includes("Admin");
}

/** 파트너 포털 경로인가 — /partner, /partner/*, /partner-settlement(/*) */
export function isPartnerPath(pathname) {
  const p = pathname || "";
  return p === "/partner" || p.startsWith("/partner/") || p === "/partner-settlement" || p.startsWith("/partner-settlement/");
}

/**
 * 파트너 레이아웃(상단 스트립 + 좌측 레일 + 파트너 하단 탭)을 쓰는가 — **역할 OR 경로** (2026-09-11, 기획 docs/ops/partner-center-access-plan.md §6).
 *   · Partner 단독 계정(isPartnerOnly): 어느 경로든(허용된 내 정보·비밀번호 화면 포함) 파트너 레이아웃 — 종전 규칙 그대로
 *   · Creator 겸 Partner(2026-09-21 허용): 파트너 경로(/partner*)에서만 파트너 레이아웃, 그 밖은 크리에이터 레이아웃
 *   · Admin(Partner 역할이 있든 없든): 파트너 경로(/partner*)에서만 파트너 레이아웃 — 대표·운영이 보는 화면 = 파트너가 보는 화면(대표 결정 ④).
 *     Partner 역할이 없는 어드민은 원장 없이 **새 파트너가 보는 둘러보기 화면**을 미리 본다(2026-09-11 대표 지적 — 종전엔 Partner 역할이 있어야만 레일이 떠서 대표 계정에 크리에이터 메뉴가 보였다)
 *   · 크리에이터(둘 다 없음)는 어디서도 거짓 — /partner 를 열면 크리에이터 레이아웃 안에서 안내 카드
 * 🔒 접근 잠금은 여기가 아니라 ProtectedRoute(isPartnerOnly 기준)다. 이 함수는 겉모습만 정한다.
 */
export function usesPartnerLayout(role, pathname) {
  const r = role || "";
  const isPartner = r.includes("Partner");
  const isAdmin = r.includes("Admin");
  if (isPartnerOnly(role)) return true;
  if (isAdmin || isPartner) return isPartnerPath(pathname);
  return false;
}

/** 어드민이 파트너 경로를 보는 중(Partner 역할 유무 무관) — 「어드민 확인용」 띠를 그리고 서명·수료 단추를 잠근다(대리 서명·대리 이수 방지, 임퍼소네이션과 같은 규칙) */
export function isPartnerAdminPreview(role, pathname) {
  const r = role || "";
  return r.includes("Admin") && isPartnerPath(pathname);
}

/**
 * 슈퍼 관리자 본인 테스트(2026-09-20 대표 지시) — Master + Partner 역할을 함께 가진 계정은 **자기 계정으로** 서명·수료·채널 등록을 끝까지 해 볼 수 있다.
 *   대리가 아니다: 서명·수료 RPC 는 로그인한 본인 이메일의 원장(partner_program)에만 기록한다. 띠는 그대로 그려 「실제로 기록된다」를 알린다.
 *   Master 가 아닌 어드민 겸직은 종전대로 잠근다.
 */
export function isPartnerMasterSelfTest(role, pathname) {
  const r = role || "";
  return isPartnerAdminPreview(role, pathname) && r.includes("Master") && r.includes("Partner");
}

/** 서명·수료 단추를 잠글 어드민인가 — 어드민 확인용이되 슈퍼 관리자 본인 테스트는 아님 */
export function isPartnerAdminLocked(role, pathname) {
  return isPartnerAdminPreview(role, pathname) && !isPartnerMasterSelfTest(role, pathname);
}

/** 어드민인데 Partner 역할이 없어 원장(partner_program)이 없는 미리보기 — 레이아웃·허브가 가짜 pending 행으로 둘러보기 화면을 그린다 */
export function isPartnerAdminPreviewWithoutRole(role, pathname) {
  const r = role || "";
  return isPartnerAdminPreview(role, pathname) && !r.includes("Partner");
}

/** 원장이 없는 어드민 미리보기용 가짜 행 — 새 파트너의 첫 화면(서명 전·유형 선언 전)과 같다. 🔒 DB 에 쓰지 않는다 */
export const PREVIEW_PROGRAM = Object.freeze({
  // 유형은 「둘 다(확인 전)」 — 어드민이 유치 현황·권리사 영업 메뉴를 모두 볼 수 있게 (2026-09-11 대표 「UTM 메뉴가 안 보인다」)
  partner_email: null, partner_type: "both", declared_type: "both", type_confirmed: false, status: "pending",
  partner_code: null, lesson_deadline: null, pending_rate: null, rate_effective_from: null,
  terms_versions_required: null, lesson_version_required: null, agreement_version_required: null, preview: true,
});

/**
 * 레일 「메뉴」 구획 — 라벨은 tt(labelKey, labelKo) 로 화면에서 옮긴다. 하단 탭은 navKey(ko/en/ja/zh.json nav.*, 영문 8자 이내).
 * types: 이 항목이 보이는 유형. "all" 은 선언 전에도 보인다.
 * mobileDrop: 휴대폰 탭이 4개를 넘을 때(둘 다) 빼는 항목 — 가이드는 홈 카드에서도 들어간다.
 */
export const PARTNER_MENU = lazyI18n(() => [
  { key: "home", path: "/partner", exact: true, icon: Home, labelKey: "a.cd07a7cd", labelKo: tt("a.cd07a7cd", "홈"), navKey: "nav.partnerHome", types: ["all"] },
  { key: "docs", path: "/partner/docs", icon: BookOpen, labelKey: "a.45831d88", labelKo: tt("a.45831d88", "파트너 가이드"), navKey: "nav.partnerGuide", types: ["all"], mobileDrop: true },
  { key: "leads", path: "/partner/leads", icon: Briefcase, labelKey: "a.7150db0f", labelKo: tt("a.7150db0f", "권리사 영업"), navKey: "nav.partnerLeads", types: ["video", "both"] },
  // 휴대폰: 둘 다 유형은 성과가 들어오며 4개를 넘으므로 유치 현황을 맨 마지막에 뺀다(홈 카드·성과 화면에서 들어간다, 2026-09-18 기획 §5)
  { key: "attr", path: "/partner/attr", icon: Users, labelKey: "a.61bc1b5d", labelKo: tt("a.61bc1b5d", "유치 현황"), navKey: "nav.partnerReferrals", types: ["marketing", "both"], mobileDrop: true, mobileDropOrder: -1 },
  // 유입 경로(2026-09-18 PR B): 링크 클릭→가입→승인→첫 채널→수익 퍼널을 경로별로. 휴대폰은 맨 먼저 뺀다(유치 현황·성과 화면에서 들어간다). docs/pages/main/PartnerSources.md
  { key: "sources", path: "/partner/sources", icon: TrendingUp, labelKey: "a.3f699429", labelKo: tt("a.3f699429", "유입 경로"), navKey: "nav.partnerSources", types: ["marketing", "both"], mobileDrop: true, mobileDropOrder: 2 },
  // 성과(2026-09-18): 내가 영업한 건의 조회수·레이블리 매출·내 몫 — 정산(돈을 신청·받는 화면)과 구분. docs/pages/main/PartnerResults.md
  { key: "results", path: "/partner/results", icon: BarChart2, labelKey: "a.b3781f65", labelKo: tt("a.b3781f65", "성과"), navKey: "nav.partnerResults", types: ["all"] },
  // 자료실 — 내 코드가 박힌 파트너 마크 내려받기 (2026-09-22, docs/ops/partner-library-plan.md). 휴대폰 4개 규칙에서는 빠지고 홈 「내 마크」 카드에서 들어간다
  { key: "library", path: "/partner/library", icon: Folder, labelKey: "a.615ac048", labelKo: tt("a.615ac048", "자료실"), navKey: "nav.partnerLibrary", types: ["all"], mobileDrop: true, mobileDropOrder: 0 },
  { key: "settle", path: "/partner-settlement", icon: DollarSign, labelKey: "a.15d47b30", labelKo: tt("a.15d47b30", "정산"), navKey: "nav.partnerSettlement", types: ["all"] },
  // 2단계 S-6(2026-09-12): 문의·소명 — 크리에이터 문의 창구(/support)를 파트너 분류 「파트너·소명」으로 재사용. 공통 약관 제14조 2항
  { key: "support", path: "/support", icon: MessageSquare, labelKey: "a.c3da7bb3", labelKo: tt("a.c3da7bb3", "문의·소명"), navKey: "nav.partnerSupport", types: ["all"], mobileDrop: true, mobileDropOrder: 1 },
]);

/** 레일 「계정」 구획 + 상단 프로필 메뉴 */
export const PARTNER_ACCOUNT_MENU = lazyI18n(() => [
  { key: "profile", path: "/edit-profile", icon: User, labelKey: "a.f6c42c77", labelKo: tt("a.f6c42c77", "내 정보") },
  { key: "password", path: "/change-password", icon: Lock, labelKey: "a.b101c881", labelKo: tt("a.b101c881", "비밀번호 변경") },
]);

/**
 * 유형별 메뉴. **선언 전(null)은 전부**(둘러보기 — 유치 현황·권리사 영업 화면이 빈 상태로 「무엇을 하게 되는지」를 보여 준다,
 * 2026-09-11 대표 「UTM 메뉴가 안 보인다」 — 종전엔 선언 전 홈·가이드·정산만이라 링크 생성기가 있는 유치 현황에 갈 수 없었다).
 * 선언 뒤엔 그 유형 것만. mobile 이면 최대 4개.
 */
export function partnerMenuFor(program, { mobile = false } = {}) {
  const type = program ? (program.type_confirmed ? program.partner_type : program.declared_type) || null : null;
  const items = PARTNER_MENU.filter((m) => m.types.includes("all") || !type || m.types.includes(type));
  if (!mobile || items.length <= 4) return items;
  // 4개가 될 때까지만 뺀다 — mobileDropOrder 가 큰 것(문의·소명)부터. 가이드는 홈 카드에서도, 문의·소명은 홈 「담당자」 카드에서도 들어간다
  const droppable = items.filter((m) => m.mobileDrop).sort((a, b) => (b.mobileDropOrder || 0) - (a.mobileDropOrder || 0));
  const out = [...items];
  for (const d of droppable) { if (out.length <= 4) break; out.splice(out.indexOf(d), 1); }
  return out;
}

/** 현재 경로가 이 메뉴 항목인가. exact 는 홈(/partner)만 — /partner/docs 가 홈으로 잡히지 않게 */
export function isPartnerMenuActive(pathname, item) {
  if (!pathname || !item) return false;
  if (item.exact) return pathname === item.path;
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

/** 프로필 단추에 쓰는 머리글자 — 이름이 없으면 이메일 첫 글자 */
export function partnerInitial({ name, email } = {}) {
  const s = String(name || email || "").trim();
  return s ? s.charAt(0).toUpperCase() : "?";
}
